import { useRoute } from '@react-navigation/native';
import { useFormikContext } from 'formik';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { SelectModal } from '@components/FormModals/SelectModal';
import Box from '@components/Restyle/Box';
import ActivityIndicatorLoading from '@components/shared/ActivityIndicatorLoading';
import { TaskFormStackScreenProps } from '@components/Tasks/TaskFormStack';
import { ProjectMemberRole, ProjectSortOption } from '@graphql/generated';
import { useListProjectsBasicInfoForEditTaskFromQuery } from '@hooks/useListProjectsBasicInfoForEditTaskFromQuery';
import { useListProjectsFromQuery } from '@hooks/useListProjectsFromQuery';
import { useAppNavigation } from '@navigation/useAppNavigation';

interface ProjectSelectProps {
  isBottomSheet?: boolean;
  isEditTask?: boolean;
  onSelectFinish?: () => void;
}

const ProjectSelect = ({
  isBottomSheet,
  isEditTask,
  onSelectFinish,
}: ProjectSelectProps) => {
  const { t } = useTranslation('models');

  const { params } =
    useRoute<TaskFormStackScreenProps<'project-select'>['route']>();

  const listProjectsBasicInfoForEditTask =
    useListProjectsBasicInfoForEditTaskFromQuery({
      sortBy: ProjectSortOption.NameAsc,
      skip: !(isEditTask || params?.isEditTask),
    });

  const listProjectsWithoutSpecifiedRoles = useListProjectsFromQuery({
    first: 25,
    sortBy: ProjectSortOption.NameAsc,
  });

  const { projects, fetchFromCursor, refreshControl, refreshing, loading } =
    isEditTask || params?.isEditTask
      ? listProjectsBasicInfoForEditTask
      : listProjectsWithoutSpecifiedRoles;

  const { setFieldValue, getFieldProps, setTouched, touched } =
    useFormikContext();
  const { name: fieldName, value } = getFieldProps('projectId');

  useEffect(() => {
    return () => setTouched({ ...touched, projectId: true });
  }, []);
  const navigation = useAppNavigation();

  const filteredProject =
    isEditTask || params?.isEditTask
      ? projects.filter((item) => {
          if (item.currentUserRole === ProjectMemberRole.Owner) {
            return true;
          } else if (item.currentUserTeamRole) {
            return true;
          } else {
            return false;
          }
        })
      : projects.filter(
          (item) => item.currentUserRole !== ProjectMemberRole.Viewer
        );

  if (loading)
    return (
      <Box
        style={{ flex: 1, justifyContent: 'center', alignContent: 'center' }}>
        <ActivityIndicatorLoading />
      </Box>
    );

  return (
    <SelectModal
      title='Project'
      list={filteredProject}
      setSelected={(id: any) => {
        setTouched({ ...touched, [fieldName]: true });
        setFieldValue(fieldName, id);
        onSelectFinish ? onSelectFinish() : navigation.goBack();
      }}
      value={value}
      singleSelect
      emptyContentMessage={t('resourceNotFoundProject.message')}
      onClose={navigation.goBack}
      isBottomSheet={isBottomSheet}
      isProjectSelect={true}
      loading={refreshing || loading}
      fetchMore={fetchFromCursor}
      refreshControl={refreshControl}
    />
  );
};

export default ProjectSelect;
